/**
 * Packet assembly for the unified school print — the fetch fan-out that turns
 * a `SchoolPrintRequest` into a `SchoolPrintJob` plus the record documents it
 * references. Two consumers: `/school/print` renders and prints the job, and
 * the print dialog assembles the SAME job in place to export it as CSV, so
 * the spreadsheet and the paper can never disagree about what a selection
 * contains.
 *
 * `request` null ⇒ every query stays idle (the dialog before Download is
 * pressed). Sections degrade independently: a records fetch failing (or the
 * campus having production records off) never blocks the pass-out sheets,
 * and vice versa — a 7:30 AM breakfast line beats a complete packet.
 */

import { useQueries } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import {
  type CafeteriaServiceSessionResponse,
  fetchServiceSession,
  fetchServiceSessions,
  serviceSessionKey,
  serviceSessionsKey,
  useServiceHistoryQuery,
} from '@/api/cafeteria'
import {
  fetchProductionRecordDocument,
  type ProductionRecordDocument,
  useProductionRecordsForSchoolQuery,
} from '@/api/production-records'
import { fetchSchoolForecastBatchesForMonth, useMySchoolAdminLocationsQuery } from '@/api/school-orders'
import { cafeteriaDeadlinePolicy, ordersConfirmedForDay } from '@/lib/cafeteria/order-window'
import {
  buildServePrintDay,
  type ServePrintDay,
  servePrintedAtLabel,
  servePrintRangeLabel,
} from '@/lib/cafeteria/print-plan'
import type { Mealtime } from '@/lib/mealtime'
import { buildAlreadyOrdered } from '@/lib/school-orders/already-ordered'
import { bandAudienceFold } from '@/lib/school-orders/columns'
import {
  buildSchoolPrintDayOptions,
  buildSchoolPrintOrderColumns,
  buildSchoolPrintOrdersDoc,
  buildSplitSchoolPrintOrdersDocs,
  recordPrintsBlank,
  type SchoolPrintJob,
  type SchoolPrintOrdersDoc,
} from '@/lib/school-print/print-plan'
import type { SchoolPrintRequest } from '@/lib/school-print/request'
import { serveDraftKey, useCafeteriaServeDraftStore } from '@/stores/cafeteria-serve-draft'

/** ISO instant bounds for the inclusive yyyy-mm-dd range (UTC calendar days). */
const dayStart = (iso: string) => `${iso}T00:00:00.000Z`
const dayEnd = (iso: string) => `${iso}T23:59:59.999Z`

const EMPTY_DAYS: readonly string[] = []

export interface SchoolPrintJobState {
  /** Null while pending, when the request is null, or when nothing is printable. */
  job: SchoolPrintJob | null
  /** Fetched record documents by id — a missing id renders an inline error band. */
  documentsById: ReadonlyMap<string, ProductionRecordDocument>
  /** Any wanted section still fetching. */
  pending: boolean
  /** The spine (service-history) failed — nothing can be assembled. */
  historyError: boolean
  /** The records list failed; the rest of the packet still assembles. */
  recordsError: Error | null
  /** Records were asked for AND the campus has production records on. */
  wantRecords: boolean
  /** Cover-page footnotes (not-yet-materialized record days). */
  footnotes: string[]
}

export function useSchoolPrintJob(request: SchoolPrintRequest | null): SchoolPrintJobState {
  const locationId = request?.locationId ?? null
  const range = request?.range ?? null
  const selectedDays = request?.selectedDays ?? EMPTY_DAYS
  const grouping = request?.grouping ?? 'buckets'
  const rosterFilter = request?.rosterFilter
  const mealtimeFilter = request?.mealtimeFilter
  const orderColumnFilter = request?.orderColumnFilter
  const splitOrdersByBucket = request?.splitOrdersByBucket ?? false

  // The spine: day skeleton + productionRecordsEnabled + the campus deadline
  // policy/timezone.
  const historyQuery = useServiceHistoryQuery(
    locationId && range ? { schoolLocationId: locationId, from: range.from, to: range.to } : null,
  )
  const productionRecordsEnabled = historyQuery.data?.productionRecordsEnabled ?? true
  const wantRecords = Boolean(request?.sections.has('records')) && productionRecordsEnabled
  const wantPassout = Boolean(request?.sections.has('passout'))
  const wantOrders = Boolean(request?.sections.has('orders'))
  const deadlinePolicy = useMemo(
    () => (historyQuery.data ? cafeteriaDeadlinePolicy(historyQuery.data.location) : null),
    [historyQuery.data],
  )

  // ---- Orders section: campus columns + combined school/parent demand. ----
  // The school shape resolves with the CAFETERIA surface's access rules
  // (worker / school-admin / ordo-admin), narrowed to this campus.
  const ordersSchoolQuery = useMySchoolAdminLocationsQuery('CAFETERIA_WORKER', locationId, {
    enabled: wantOrders && Boolean(locationId),
  })
  const ordersSchool = useMemo(
    () => (ordersSchoolQuery.data?.me?.schoolAdminLocations ?? []).find((s) => s.id === locationId) ?? null,
    [ordersSchoolQuery.data, locationId],
  )
  // A ≤14-day range spans at most two calendar months.
  const ordersMonths = useMemo(() => {
    if (!range) {
      return []
    }
    const seen = new Map<string, { year: number; month: number }>()
    for (const iso of range.days) {
      const key = iso.slice(0, 7)
      if (!seen.has(key)) {
        seen.set(key, { year: Number(iso.slice(0, 4)), month: Number(iso.slice(5, 7)) })
      }
    }
    return [...seen.values()]
  }, [range])
  const ordersBatchQueries = useQueries({
    queries: (wantOrders && locationId ? ordersMonths : []).map((m) => ({
      queryKey: ['school-orders', 'forecast-batches', { schoolLocationId: locationId ?? '', ...m }],
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        fetchSchoolForecastBatchesForMonth({ schoolLocationId: locationId ?? '', ...m }, signal),
      staleTime: 15 * 1000,
    })),
    combine: (results) => ({
      batches: results.flatMap((result) => result.data?.schoolForecastBatchesForMonth ?? []),
      familyOrders: results.flatMap((result) => result.data?.familyOrders ?? []),
      pending: results.some((result) => result.isPending),
    }),
  })
  // AGE_GROUP campuses print in the locked portion bands: the union of the
  // lunch-style and breakfast-style partitions (an untagged menu set spans
  // every partition band), deduped by column — so both K-8|9-12 lunch lines
  // and K-12 breakfast lines have a column. Other dimensions keep their
  // campus vocabulary columns; private campuses print the single column.
  // (Shared with the dialog's filter chips — buildSchoolPrintOrderColumns.)
  const ordersColumns = useMemo(() => (ordersSchool ? buildSchoolPrintOrderColumns(ordersSchool) : []), [ordersSchool])
  const ordersLookup = useMemo(
    () =>
      buildAlreadyOrdered(ordersBatchQueries.batches, {
        wholeSchool: ordersSchool?.orderGroupBy === 'NONE',
        familyOrders: ordersBatchQueries.familyOrders,
        combineFamilyIntoTotals: true,
        ...(ordersSchool ? { foldTierAudience: bandAudienceFold(ordersSchool) } : {}),
      }),
    [ordersBatchQueries.batches, ordersBatchQueries.familyOrders, ordersSchool],
  )
  // Item names per (day, mealtime) — batch lines + family cells both arrive
  // with server-rendered (chef-swap-aware) names, so no menus fetch needed.
  const ordersNamesByDayMeal = useMemo(() => {
    const index = new Map<string, Map<string, string>>()
    const add = (iso: string, mealtime: Mealtime, menuItemId: string, name: string | null | undefined): void => {
      const key = `${iso}|${mealtime}`
      const names = index.get(key) ?? new Map<string, string>()
      if (!names.has(menuItemId) || (name && names.get(menuItemId) === 'Item')) {
        names.set(menuItemId, name ?? 'Item')
      }
      index.set(key, names)
    }
    for (const batch of ordersBatchQueries.batches) {
      if (batch.status === 'VOIDED' || batch.status === 'DRAFT') {
        continue
      }
      for (const item of batch.items) {
        // Keyed like `buildAlreadyOrdered` — by the successor item when a
        // dated template release replaced the line's menu (PROD-1515).
        add(batch.serviceDate.slice(0, 10), batch.mealtime, item.currentMenuItemId ?? item.menuItemId, item.itemName)
      }
    }
    for (const cell of ordersBatchQueries.familyOrders) {
      add(cell.serviceDate.slice(0, 10), cell.mealtime, cell.currentMenuItemId ?? cell.menuItemId, cell.itemName)
    }
    return index
  }, [ordersBatchQueries.batches, ordersBatchQueries.familyOrders])

  // ---- Records section: list + per-document fan-out (batch-page pattern). --
  const listQuery = useProductionRecordsForSchoolQuery({
    schoolLocationId: wantRecords && locationId ? locationId : '',
    fromDate: wantRecords && range ? dayStart(range.from) : null,
    toDate: wantRecords && range ? dayEnd(range.to) : null,
    limit: 100,
  })
  // The dialog's Mealtimes row narrows the records the same way it narrows
  // the pass-out sheets below: an empty filter is every mealtime.
  const recordRows = useMemo(() => {
    const rows = listQuery.data?.productionRecordsForSchool ?? []
    return rows.filter(
      (row) =>
        selectedDays.includes(row.serviceDate.slice(0, 10)) &&
        (!mealtimeFilter || mealtimeFilter.size === 0 || mealtimeFilter.has(row.mealtime)),
    )
  }, [listQuery.data, selectedDays, mealtimeFilter])
  const documentQueries = useQueries({
    queries: recordRows.map((record) => ({
      queryKey: ['production-records', 'document', record.id],
      queryFn: ({ signal }: { signal?: AbortSignal }) => fetchProductionRecordDocument(record.id, signal),
      staleTime: 30 * 1000,
    })),
    combine: (results) => ({
      byId: new Map(
        results.flatMap((result, index) => (result.data ? [[recordRows[index]!.id, result.data] as const] : [])),
      ),
      pending: results.some((result) => result.isPending),
    }),
  })

  // ---- Pass-out section: sessions + per-(day, mealtime) details. ----------
  const sessionsQueries = useQueries({
    queries: (wantPassout && locationId ? selectedDays : []).map((iso) => ({
      queryKey: serviceSessionsKey({ schoolLocationId: locationId ?? '', serviceDate: iso }),
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        fetchServiceSessions({ schoolLocationId: locationId ?? '', serviceDate: iso }, signal),
      staleTime: 15 * 1000,
    })),
    combine: (results) => ({
      byIso: new Map(results.map((result, index) => [selectedDays[index]!, result.data?.sessions ?? []] as const)),
      pending: results.some((result) => result.isPending),
    }),
  })
  const wantedSessions = useMemo(
    () =>
      !wantPassout || !locationId
        ? []
        : selectedDays.flatMap((iso) =>
            (sessionsQueries.byIso.get(iso) ?? [])
              .filter((session) => !mealtimeFilter || mealtimeFilter.size === 0 || mealtimeFilter.has(session.mealtime))
              .map((session) => ({ iso, mealtime: session.mealtime })),
          ),
    [wantPassout, locationId, selectedDays, sessionsQueries.byIso, mealtimeFilter],
  )
  const detailQueries = useQueries({
    queries: wantedSessions.map(({ iso, mealtime }) => ({
      queryKey: serviceSessionKey({ schoolLocationId: locationId ?? '', serviceDate: iso, mealtime }),
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        fetchServiceSession({ schoolLocationId: locationId ?? '', serviceDate: iso, mealtime }, signal),
      staleTime: 10 * 1000,
    })),
    combine: (results) => ({
      byIso: results.reduce((acc, result) => {
        if (result.data) {
          acc.set(result.data.serviceDate, [...(acc.get(result.data.serviceDate) ?? []), result.data])
        }
        return acc
      }, new Map<string, CafeteriaServiceSessionResponse[]>()),
      pending: results.some((result) => result.isPending),
      error: results.some((result) => result.isError),
    }),
  })

  // Per-(day, mealtime) serve drafts — the store is keyed
  // (locationId, serviceDate, mealtime), so each mealtime's roster reads its
  // own draft rather than replaying one mealtime's toggles across the day.
  const serveOverridesFor = useCallback(
    (iso: string) =>
      (mealtime: string): { servedOverrides: Record<string, boolean>; quantityServed: Record<string, number> } => {
        const draft = useCafeteriaServeDraftStore.getState().getDraft(serveDraftKey(locationId ?? '', iso, mealtime))
        return { servedOverrides: draft.servedOverrides, quantityServed: draft.quantityServed }
      },
    [locationId],
  )

  // ---- Job assembly --------------------------------------------------------
  const pending =
    Boolean(request) &&
    (historyQuery.isPending ||
      (wantRecords && (listQuery.isPending || documentQueries.pending)) ||
      (wantPassout && (sessionsQueries.pending || detailQueries.pending)) ||
      (wantOrders && (ordersBatchQueries.pending || ordersSchoolQuery.isPending)))

  const job = useMemo<SchoolPrintJob | null>(() => {
    if (!locationId || !range || pending || !historyQuery.data) {
      return null
    }
    /** The day's item names, re-keyed mealtime → menuItemId → name. */
    const ordersNamesForDay = (iso: string): ReadonlyMap<Mealtime, ReadonlyMap<string, string>> => {
      const namesByMealtime = new Map<Mealtime, ReadonlyMap<string, string>>()
      for (const [key, names] of ordersNamesByDayMeal) {
        if (key.startsWith(`${iso}|`)) {
          namesByMealtime.set(key.slice(iso.length + 1) as Mealtime, names)
        }
      }
      return namesByMealtime
    }
    const buildOrdersDoc = (iso: string): SchoolPrintOrdersDoc | null => {
      if (!wantOrders || ordersColumns.length === 0) {
        return null
      }
      // Pure core lives in print-plan.ts (where unattributed lines earn their
      // Unassigned catch column, and the bucket filter subsets columns +
      // totals) so the row/total math is unit-testable.
      return buildSchoolPrintOrdersDoc({
        iso,
        columns: ordersColumns,
        lookup: ordersLookup,
        namesByMealtime: ordersNamesForDay(iso),
        ordersConfirmed: ordersConfirmedForDay(iso, deadlinePolicy),
        columnFilter: orderColumnFilter ?? new Set<string>(),
      })
    }
    /**
     * One scoped doc per bucket in scope — a split day's sheets. Each is an
     * ordinary single-column filtered build (same row/total math, same
     * "Includes only" header), so a per-homeroom sheet is a per-homeroom
     * FILTER rendered on its own block. Buckets with nothing ordered that day
     * build null and drop out: no blank paper per empty homeroom.
     *
     * Unattributed lines (staff / no-group) belong to no bucket, so — exactly
     * as with the existing filter — they appear on NO split sheet. That is why
     * the dialog warns before turning this on.
     */
    const buildSplitOrdersDocs = (iso: string): SchoolPrintOrdersDoc[] =>
      !wantOrders || ordersColumns.length === 0
        ? []
        : buildSplitSchoolPrintOrdersDocs({
            iso,
            columns: ordersColumns,
            lookup: ordersLookup,
            namesByMealtime: ordersNamesForDay(iso),
            ordersConfirmed: ordersConfirmedForDay(iso, deadlinePolicy),
            columnFilter: orderColumnFilter ?? new Set<string>(),
          })

    const days: SchoolPrintJob['days'] = []
    for (const iso of selectedDays) {
      const records = wantRecords
        ? recordRows
            .filter((row) => row.serviceDate.slice(0, 10) === iso)
            .map((row) => ({
              id: row.id,
              mealtime: row.mealtime,
              vendorName: row.vendorLocation?.name ?? 'Kitchen',
              blank: recordPrintsBlank(row.status),
            }))
        : []
      let serveDay: ServePrintDay | null = null
      if (wantPassout) {
        const details = detailQueries.byIso.get(iso) ?? []
        const order = (sessionsQueries.byIso.get(iso) ?? []).map((session) => session.mealtime)
        const ordered = order.length > 0 ? order.flatMap((m) => details.filter((d) => d.mealtime === m)) : details
        serveDay = buildServePrintDay(
          iso,
          ordered,
          serveOverridesFor(iso),
          ordersConfirmedForDay(iso, deadlinePolicy),
          grouping,
          rosterFilter,
        )
      }
      // Split or combined, never both — see SchoolPrintJobDay.
      const orderSheets = wantOrders && splitOrdersByBucket ? buildSplitOrdersDocs(iso) : []
      const orders = wantOrders && !splitOrdersByBucket ? buildOrdersDoc(iso) : null
      if (records.length > 0 || serveDay || orders || orderSheets.length > 0) {
        days.push({ iso, records, serveDay, orders, orderSheets })
      }
    }
    if (days.length === 0) {
      return null
    }
    return {
      schoolName: historyQuery.data.location.schoolName || historyQuery.data.location.locationName,
      rangeLabel: servePrintRangeLabel(selectedDays),
      printedAtLabel: servePrintedAtLabel(),
      grouping,
      days,
    }
  }, [
    locationId,
    range,
    pending,
    historyQuery.data,
    selectedDays,
    wantRecords,
    wantPassout,
    wantOrders,
    recordRows,
    detailQueries.byIso,
    sessionsQueries.byIso,
    serveOverridesFor,
    deadlinePolicy,
    grouping,
    rosterFilter,
    ordersColumns,
    ordersLookup,
    ordersNamesByDayMeal,
    orderColumnFilter,
    splitOrdersByBucket,
  ])

  const footnotes = useMemo(() => {
    if (!historyQuery.data || !wantRecords) {
      return []
    }
    const notMaterialized = buildSchoolPrintDayOptions(historyQuery.data.days, selectedDays).filter(
      (option) => option.notMaterialized,
    )
    return notMaterialized.length > 0
      ? [
          'Some days have orders but no record form yet — forms appear as the service day approaches. Reprint closer to the date.',
        ]
      : []
  }, [historyQuery.data, wantRecords, selectedDays])

  return {
    job,
    documentsById: documentQueries.byId,
    pending,
    historyError: historyQuery.isError,
    recordsError: wantRecords ? (listQuery.error ?? null) : null,
    wantRecords,
    footnotes,
  }
}
